import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fmtDate as regionDate, todayISO } from '../../utils/region';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useStore } from '../../store';
import { api } from '../../utils/api';
import { LevelLabel } from '../Jobs';
import { splitCommentItems } from '../../utils/richText';
import { jobTitleLabel } from '../../utils/displayName';
import PopupHomeNotice from '../PopupHomeNotice';
import { usePopupHome, HOME_JOBS, POPUP_REFRESH } from '../../services/popupHome';   // Active Jobs, one tab only (report c2846304)

// ── Passport received on an 11-Month extension (Calem, report c2de0118) ────────────────────────────
// "When we take a passport for an extension (11 months) from the client (or third-party individual), have
// a pop-up ask me (only for the time being) to change the job from awaiting immigration to in process and
// allow 1 week for finish date. Take note of the comments column to see whether there are any associated
// re-entry stamps included." Such a passport, saved in Document Tracking, no longer moves the job: it
// leaves the person who saved it a prompt on the server (services/trPassportReturnsJob.ts), and this pops
// it up — the job, the finish date it would get, and both comments (the job's and the Document Tracking
// record's) with every re-entry mention marked. Move to In Process sets 2. In Process with a finish date one
// week from today; Not now drops the prompt and leaves the job alone. Neither emails the client.
// Mounted once, app-wide, beside JobStatusConfirm. It looks when Document Tracking says a record, its job
// links or a confirmed proposal were just saved (the event below), and again when the user moves around or
// comes back to the tab, so a prompt left unanswered is not lost.

const EVENT = 'slf:tr-passport-prompts';
// Don't steal the keyboard from someone mid-sentence — keyed on keystrokes, as JobStatusConfirm is.
const TYPING_IDLE_MS = 4000;
const IN_PROCESS = '2. In Process';
// "re-entry", "reentry", "re entry", "Re-Entries" — wherever it sits in a line. The class also takes the
// typographic hyphens and dashes pasted from Word or a PDF: U+2010 to U+2015, written raw.
const REENTRY_SPLIT = /(re[\s‐-―-]?entr(?:y|ies))/gi;
const REENTRY_TEST = /re[\s‐-―-]?entr(?:y|ies)/i;
const fmtDate = (ymd: string) => (ymd ? regionDate(ymd) : '—');
// The day the passport was taken, in the region's date format, in the tenant's zone.
const fmtDay = (ts: number) => (ts ? fmtDate(todayISO(new Date(ts * 1000))) : '—');

// One comment line with each re-entry mention marked.
function Marked({ text }: { text: string }) {
  return (
    <>
      {text.split(REENTRY_SPLIT).map((p, i) => (i % 2
        ? <mark key={i} style={{ background: '#FEF3C7', color: '#92400E', fontWeight: 800, borderRadius: 4, padding: '0 3px' }}>{p}</mark>
        : <React.Fragment key={i}>{p}</React.Fragment>))}
    </>
  );
}

export default function TrPassportPrompt() {
  const { t } = useTranslation();
  const loc = useLocation();
  const me = useStore(s => s.me);
  const showToast = useStore(s => s.showToast);
  // Never on the public/no-auth pages: a client on /w/<token> must not see the office's jobs.
  const publicPage = /^\/(login|w|dd|contract|portal)(\/|$)/.test(loc.pathname);
  const [typing, setTyping] = useState(false);
  const lastKey = useRef(0);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const tag = (el?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || el?.isContentEditable) { lastKey.current = Date.now(); setTyping(true); }
    };
    document.addEventListener('keydown', onKey, true);
    const id = setInterval(() => setTyping(Date.now() - lastKey.current < TYPING_IDLE_MS), 1000);
    return () => { document.removeEventListener('keydown', onKey, true); clearInterval(id); };
  }, []);
  const [queue, setQueue] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const meRef = useRef(me);
  meRef.current = me;

  const poll = useCallback(async () => {
    const m = meRef.current;
    // Only people who can open Jobs are ever asked (the gate on /api/jobs): anyone else's call would be
    // refused, and a refused call pages the admins.
    if (!m || !localStorage.getItem('mf_token') || !(m.modules || []).some(k => k === 'active_jobs' || k === 'closed_jobs')) return;
    try {
      const { rows } = await api.trPassportPrompts();
      setQueue(rows || []);
    } catch { /* transient — try again on the next look */ }
  }, []);

  // Drop everything the moment the user logs out; look again when they sign in or move around…
  useEffect(() => { if (!me) setQueue([]); }, [me]);
  useEffect(() => { poll(); }, [loc.pathname, me?.userId, poll]);
  // …the moment Document Tracking has saved, when they come back to this tab, and when a card for Active Jobs
  // has just been clicked in another tab (POPUP_REFRESH, report 3f8007d0).
  useEffect(() => {
    const now = () => { void poll(); };
    const onVisible = () => { if (document.visibilityState === 'visible') void poll(); };
    window.addEventListener(EVENT, now);
    window.addEventListener(POPUP_REFRESH, now);
    document.addEventListener('visibilitychange', onVisible);
    return () => { window.removeEventListener(EVENT, now); window.removeEventListener(POPUP_REFRESH, now); document.removeEventListener('visibilitychange', onVisible); };
  }, [poll]);

  const current = queue[0];
  // Report c2846304: the job moves on Active Jobs, so the question is asked in the tab showing that page and
  // in no other; with the page open in no tab, the tab in front offers to open it (PopupHomeNotice).
  const pending = !!me && !!current && !publicPage && !typing;
  const home = usePopupHome(HOME_JOBS, pending, queue.map(x => String(x.jobId)));
  if (!pending) return null;
  if (home !== 'show') return home === 'notice' ? <PopupHomeNotice page={HOME_JOBS} /> : null;

  const jobNo = `#${Number(current.jobNo).toLocaleString()}`;
  const from = current.fromName
    ? t(current.fromKind === 'client' ? 'trp_from_client' : 'trp_from_third', { name: current.fromName })
    : t(current.fromKind === 'client' ? 'tr_cp_client' : 'tr_cp_third');
  const oldFinish = current.finishUnknown ? t('jc_unknown') : (String(current.finishDate || '').trim() ? fmtDate(current.finishDate) : '—');
  // The Active Jobs comment as its lines (paragraphs or bullets); the Document Tracking one is plain text.
  const jobLines = splitCommentItems(String(current.jobComments || '')).map(i => i.text).filter(Boolean);
  const trLines = String(current.trComments || '').split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  const reentry = jobLines.concat(trLines).some(l => REENTRY_TEST.test(l));
  const drop = (jobId: string) => setQueue(q => q.filter(x => x.jobId !== jobId));

  const onApply = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.trPassportPromptApply(current.jobId);
      showToast(r?.applied ? t('trp_applied', { job: jobNo, date: fmtDate(r.finishDate) }) : t('trp_not_applied', { job: jobNo }));
      // The Jobs list, and the job's panel if it is open, show the new status and date at once.
      if (r?.applied) window.dispatchEvent(new CustomEvent('slf:jobs-changed', { detail: { jobId: current.jobId } }));
      drop(current.jobId);
    } catch (e: any) { showToast(String(e?.message || e)); }
    finally { setBusy(false); }
  };
  const onNotNow = async () => {
    if (busy) return;
    setBusy(true);
    try { await api.trPassportPromptDismiss(current.jobId); } catch { /* the next look shows it again */ }
    drop(current.jobId);
    setBusy(false);
  };

  const kv = (label: string, value: React.ReactNode) => (
    <tr>
      <td style={{ padding: '4px 14px 4px 0', color: 'var(--text-tertiary)', whiteSpace: 'nowrap', verticalAlign: 'top', fontSize: 12 }}>{label}</td>
      <td style={{ padding: '4px 0', fontSize: 13, color: 'var(--text-primary)' }}>{value}</td>
    </tr>
  );
  const comments = (label: string, lines: string[]) => (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', marginBottom: 4 }}>{label}</div>
      <div style={{ padding: '8px 11px', borderRadius: 10, background: 'var(--c-gray-50)', border: '1px solid var(--border)', fontSize: 12.5, lineHeight: 1.5, color: lines.length ? 'var(--text-primary)' : 'var(--text-tertiary)', maxHeight: 150, overflowY: 'auto', wordBreak: 'break-word' }}>
        {lines.length ? lines.map((l, i) => <div key={i}><Marked text={l} /></div>) : t('trp_no_comments')}
      </div>
    </div>
  );
  const btn: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 9, fontWeight: 600, fontSize: 13, cursor: busy ? 'not-allowed' : 'pointer' };

  return (
    // Same layer as the other job pop-ups, mounted between JobCommentPing and JobStatusConfirm (App.tsx):
    // a status email waiting on its confirmation stays on top of this.
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 5500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div role="dialog" aria-modal="true" style={{ background: 'var(--bg-surface)', borderRadius: 14, width: 500, maxWidth: '94vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-md)' }}>
        <div style={{ padding: '18px 22px 10px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <i className="ti ti-id-badge" style={{ fontSize: 22, color: 'var(--accent)' }} aria-hidden />
            <div style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1.4 }}>{t('trp_title')}</div>
            {queue.length > 1 && <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', background: 'var(--c-gray-100)', borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap' }}>1 / {queue.length}</span>}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 8, lineHeight: 1.5 }}>{t('trp_subtitle', { from, date: fmtDay(Number(current.takenAt) || 0) })}</div>
        </div>

        <div style={{ padding: '14px 22px 4px' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}><tbody>
            {kv(t('jc_job'), `${jobNo} · ${jobTitleLabel(current.specific) || '—'}`)}
            {kv(t('jc_client'), String(current.client || '').trim() || '—')}
            {kv(t('trp_status'), (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <LevelLabel v={current.level} />
                <span style={{ color: 'var(--text-tertiary)' }}>→</span>
                <b style={{ color: 'var(--accent-text)' }}><LevelLabel v={IN_PROCESS} /></b>
              </span>
            ))}
            {kv(t('jc_finish'), (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ background: '#FEF3C7', color: '#92400E', border: '1px solid #FCD34D', borderRadius: 6, padding: '2px 9px', fontWeight: 800 }}>{fmtDate(current.newFinish)}</span>
                <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t('trp_one_week')} · {t('jc_was', { v: oldFinish })}</span>
              </span>
            ))}
          </tbody></table>

          {comments(t('trp_job_comments'), jobLines)}
          {comments(t('trp_tr_comments'), trLines)}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginTop: 10, fontSize: 11.5, lineHeight: 1.5, fontWeight: reentry ? 700 : 400, color: reentry ? '#92400E' : 'var(--text-tertiary)' }}>
            <i className={reentry ? 'ti ti-alert-triangle' : 'ti ti-info-circle'} style={{ fontSize: 14, marginTop: 1 }} aria-hidden />
            {t(reentry ? 'trp_reentry_found' : 'trp_reentry_none')}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, padding: '14px 22px 20px', flexWrap: 'wrap' }}>
          <button onClick={onApply} disabled={busy} style={{ ...btn, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, opacity: busy ? 0.7 : 1 }}>
            <i className="ti ti-check" style={{ fontSize: 14 }} aria-hidden />{t('trp_apply')}
          </button>
          <button onClick={onNotNow} disabled={busy} style={{ ...btn, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-primary)' }}>
            <i className="ti ti-x" style={{ fontSize: 14 }} aria-hidden />{t('trp_not_now')}
          </button>
        </div>
      </div>
    </div>
  );
}
